import { Box, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { _getUserEntities } from '@/api/user-dashboard/queries';
import { Button, Modal, Select, type SelectOption, Separator, Typography } from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { PagePaths } from '@/routes/page-paths';
import { colors } from '@/shared/design-system/colors';
import { spacing } from '@/shared/design-system/spacing';
import { useSelectedUserConfigSlice } from '@/stores/selectors';

export const CreateNewFundModal = () => {
  const navigate = useNavigate();
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const { open, handleClose } = useGlobalModal('CREATE_NEW_FUND_MODAL');
  const { data, isLoading } = useQuery(_getUserEntities());
  const { setSelectedEntityId, setSelectedUserRole } = useSelectedUserConfigSlice();

  const entityOptions: SelectOption[] = (data?.entities ?? []).map((e) => ({
    value: e.entityId,
    label: e.entityName,
  }));

  const handleContinue = () => {
    handleClose();
    setSelectedUserRole('entity');
    setSelectedEntityId(selectedEntity);
    navigate(PagePaths.setupFund);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title="Create New Fund"
      showClose
      maxWidth={516}
    >
      <Stack flexDirection="column" gap="24px">
        <Separator />
        <Box>
          <Typography variant="body1" fontWeight={500} style={{ marginBottom: spacing[8] }}>
            Select Legal Entity
          </Typography>
          <Typography variant="body3" color={colors.text.secondary}>
            Before creating a new fund, please choose the Legal Entity that this fund will be
            associated with. <br /> You’ll be able to proceed with the full fund setup after
            selecting an entity.
          </Typography>
        </Box>
        <Select
          size="lg"
          showSearch
          options={entityOptions ?? []}
          value={selectedEntity ?? null}
          onChange={setSelectedEntity}
          disabled={isLoading}
          placeholder="Legal Entity"
        />

        <Button
          size="lg"
          fullWidth
          onClick={handleContinue}
          disabled={!selectedEntity || isLoading}
        >
          Continue
        </Button>
      </Stack>
    </Modal>
  );
};
